/**
 * Localização. Nenhum texto visível deve ser hardcoded nos componentes — use i18n.t().
 * Para adicionar um idioma: crie o dicionário em ./locales, importe e registre em `dictionaries`.
 */
import { en } from "./locales/en";
import { ptBR } from "./locales/pt-BR";

export type Locale =
  | "en" | "pt-BR" | "es" | "fr" | "de" | "it"
  | "ja" | "ko" | "zh-CN" | "zh-TW" | "ru" | "tr" | "pl" | "id";

export type Dict = Record<string, string>;

/** Nome nativo de cada idioma (para o seletor). Todos previstos; só os com dicionário ficam ativos. */
export const LOCALE_NAMES: Record<Locale, string> = {
  en: "English",
  "pt-BR": "Português (BR)",
  es: "Español",
  fr: "Français",
  de: "Deutsch",
  it: "Italiano",
  ja: "日本語",
  ko: "한국어",
  "zh-CN": "简体中文",
  "zh-TW": "繁體中文",
  ru: "Русский",
  tr: "Türkçe",
  pl: "Polski",
  id: "Bahasa Indonesia",
};

/** Dicionários carregados (idiomas disponíveis hoje). */
const dictionaries: Partial<Record<Locale, Dict>> = {
  en,
  "pt-BR": ptBR,
};

const STORAGE_KEY = "voidhunter.locale";
const listeners = new Set<() => void>();

function detectInitial(): Locale {
  try {
    const saved = localStorage.getItem(STORAGE_KEY) as Locale | null;
    if (saved && dictionaries[saved]) return saved;
  } catch { /* ignore */ }
  const nav = (navigator.language || "en") as string;
  if (dictionaries[nav as Locale]) return nav as Locale;
  if (nav.toLowerCase().startsWith("pt")) return "pt-BR";
  return "en";
}

let current: Locale = detectInitial();

export const i18n = {
  get locale(): Locale {
    return current;
  },

  /** Idiomas com dicionário disponível (selecionáveis). */
  availableLocales(): Locale[] {
    return Object.keys(dictionaries) as Locale[];
  },

  setLocale(locale: Locale): void {
    if (!dictionaries[locale] || locale === current) return;
    current = locale;
    try { localStorage.setItem(STORAGE_KEY, locale); } catch { /* ignore */ }
    listeners.forEach((fn) => fn());
  },

  /** Traduz uma chave; cai no inglês e por fim na própria chave. Suporta {param}. */
  t(key: string, params?: Record<string, string | number>): string {
    const dict = dictionaries[current] ?? en;
    let str = dict[key] ?? en[key] ?? key;
    if (params) {
      for (const [k, v] of Object.entries(params)) {
        str = str.replace(new RegExp(`\\{${k}\\}`, "g"), String(v));
      }
    }
    return str;
  },

  /** Assina mudanças de idioma; retorna o unsubscribe. */
  onChange(fn: () => void): () => void {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },
};
